import type { SchemaField, SyncrelEntity, SyncrelRelation, SyncrelState } from "../domain/types";
import { activeFrameworkFields } from "../domain/framework-fields";
import { frameworkViewState } from "../domain/framework-scope";
import { currencyFormat, escapeHtml, numberFormat } from "./render-utils";

const colorByType: Record<string, string> = {
  goal: "map-card--goal",
  actor: "map-card--actor",
  impact: "map-card--impact",
  feature: "map-card--feature",
  note: "map-card--note",
  deliverable: "map-card--deliverable",
  feedback: "map-card--note",
};

// Card geometry. Anchors are the card centre offsets used to draw relations.
export const CARD_WIDTH = 184;
export const CARD_ANCHOR_X = CARD_WIDTH / 2; // 92
export const CARD_ANCHOR_Y = 42;
export const MAP_WORLD_WIDTH = 1100;
export const MAP_WORLD_HEIGHT = 780;
export const MAP_INFINITE_CANVAS_SIZE = 1000000;
export const MAP_INFINITE_CANVAS_ORIGIN = MAP_INFINITE_CANVAS_SIZE / 2;
export const MAP_PAN_MARGIN = MAP_INFINITE_CANVAS_ORIGIN;

export const renderSpatialMap = (state: SyncrelState): string => {
  const view = frameworkViewState(state);
  const selectedId = view.workspace.selectedEntityId;
  const selectedEntity = selectedId ? view.entities.find((entity) => entity.id === selectedId) ?? null : null;
  const selectedRelation = view.relations.find((relation) => relation.id === view.workspace.selectedRelationId);
  const relations = view.relations.map((relation) => {
    const from = view.entities.find((entity) => entity.id === relation.fromId);
    const to = view.entities.find((entity) => entity.id === relation.toId);
    if (!from || !to) return "";

    const fx = from.position.x + CARD_ANCHOR_X;
    const fy = from.position.y + CARD_ANCHOR_Y;
    const tx = to.position.x + CARD_ANCHOR_X;
    const ty = to.position.y + CARD_ANCHOR_Y;
    const dx = tx - fx;
    const dy = ty - fy;
    const len = Math.hypot(dx, dy) || 1;
    // Pull the endpoints back so the line begins/ends near the card edges
    // and the arrowhead stays visible instead of hiding under the target card.
    const startGap = Math.min(len / 2 - 1, 14);
    const endGap = Math.min(len / 2 - 1, 56);
    const x1 = fx + (dx / len) * startGap;
    const y1 = fy + (dy / len) * startGap;
    const x2 = tx - (dx / len) * endGap;
    const y2 = ty - (dy / len) * endGap;
    const selected = relation.id === state.workspace.selectedRelationId;

    return `
      <line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" class="map-link ${selected ? "is-selected" : ""}" marker-end="url(#map-arrowhead)" />
      <line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" class="map-link-hit" data-relation-id="${escapeHtml(relation.id)}" />
      <text x="${(x1 + x2) / 2}" y="${(y1 + y2) / 2 - 4}" text-anchor="middle" class="map-link-label ${selected ? "is-selected" : ""}">${escapeHtml(relation.label)}</text>
    `;
  }).join("");

  return `
    <section class="panel panel--map">
      <div class="panel-head">
        <div>
          <h2>Spatial Map</h2>
          <span>${view.entities.length} entities - ${view.relations.length} relations</span>
        </div>
        <form class="connect-form" data-connect-form title="Pick a type, then drag a card's + handle onto another card - or use this fallback.">
          <select name="targetId" aria-label="Relation target">
            ${view.entities.map((entity) => `<option value="${escapeHtml(entity.id)}">${escapeHtml(entity.name)}</option>`).join("")}
          </select>
          <select name="type" data-link-type aria-label="Relation type">
            ${relationTypeOptions(state)}
          </select>
          <button type="submit">Link</button>
        </form>
      </div>
      <div
        class="map-surface"
        data-map-surface
        data-infinite-canvas="true"
        data-pan-surface="all-tools"
        data-pan-origin="centered"
        data-pan-margin="${MAP_PAN_MARGIN}"
        data-zoom="1"
        data-camera-x="${MAP_WORLD_WIDTH / 2}"
        data-camera-y="${MAP_WORLD_HEIGHT / 2}"
      >
        <div class="map-world-frame" data-map-world-frame>
          <div class="map-world" data-map-world>
        <svg class="map-links" viewBox="-${MAP_INFINITE_CANVAS_ORIGIN} -${MAP_INFINITE_CANVAS_ORIGIN} ${MAP_INFINITE_CANVAS_SIZE} ${MAP_INFINITE_CANVAS_SIZE}" preserveAspectRatio="none" aria-hidden="true">
          <defs>
            <marker id="map-arrowhead" markerWidth="9" markerHeight="9" refX="7" refY="3.2" orient="auto" markerUnits="userSpaceOnUse">
              <path d="M0,0 L7,3.2 L0,6.4 Z" class="map-arrow" />
            </marker>
          </defs>
          ${relations}
        </svg>
        ${view.entities.map((entity) => renderCard(entity, entity.id === selectedId)).join("")}
        ${view.entities.length === 0 ? renderMapEmptyState() : ""}
          </div>
        </div>
        <div class="map-hint" data-map-hint><b>Scroll</b> to zoom - <b>Double-click</b> to create - drag empty canvas to pan forever - drag <b>+</b> to link</div>
      </div>
      <div class="map-overlay" data-map-overlay aria-hidden="true">
        <div class="axis axis--x">value / strategic pull</div>
        <div class="axis axis--y">effort / dependency pressure</div>
      </div>
      ${selectedEntity ? renderEntityInspector(view, selectedEntity) : ""}
      ${selectedRelation ? renderRelationInspector(view, selectedRelation) : ""}
    </section>
  `;
};

const renderCard = (entity: SyncrelEntity, selected: boolean): string => {
  const impact = typeof entity.fields.impactScore === "number" ? entity.fields.impactScore : 0;
  const score = typeof entity.fields.riceScore === "number" ? entity.fields.riceScore : null;
  const revenue = typeof entity.fields.weightedPipelineValue === "number"
    ? entity.fields.weightedPipelineValue
    : typeof entity.fields.contractValue === "number"
      ? entity.fields.contractValue
      : null;
  const scale = Math.min(1, impact / 8);
  return `
    <article
      class="map-card ${colorByType[entity.type] ?? "map-card--feature"} ${selected ? "is-selected" : ""}"
      style="left:${entity.position.x}px; top:${entity.position.y}px; --weight:${scale};"
      data-card-id="${escapeHtml(entity.id)}"
    >
      <div class="map-card__type">${escapeHtml(entity.type)}</div>
      ${selected
        ? `<input class="map-card__title-input" data-inline-title="${escapeHtml(entity.id)}" aria-label="Rename ${escapeHtml(entity.name)}" value="${escapeHtml(entity.name)}" />`
        : `<h3>${escapeHtml(entity.name)}</h3>`}
      <div class="map-card__meta">
        ${score === null ? "" : `<span>RICE ${numberFormat.format(score)}</span>`}
        ${revenue === null ? "" : `<span>${currencyFormat.format(revenue)}</span>`}
      </div>
      <div class="map-card__bar"><span style="width:${Math.max(8, scale * 100)}%"></span></div>
      ${selected ? `<button type="button" class="map-card__delete" data-delete-entity="${escapeHtml(entity.id)}" title="Delete ${escapeHtml(entity.name)}" aria-label="Delete ${escapeHtml(entity.name)}">x</button>` : ""}
      ${selected ? `<button type="button" class="map-card__spec" data-open-spec="${escapeHtml(entity.id)}" title="${escapeHtml(specActionLabel(entity))}" aria-label="${escapeHtml(specActionLabel(entity))}">Spec</button>` : ""}
      <button type="button" class="map-card__quick map-card__quick--right" data-quick-source="${escapeHtml(entity.id)}" data-quick-create="right" title="Create linked node to the right" aria-label="Create linked node to the right">+</button>
      <button type="button" class="map-card__quick map-card__quick--bottom" data-quick-source="${escapeHtml(entity.id)}" data-quick-create="bottom" title="Create linked node below" aria-label="Create linked node below">+</button>
      <span class="map-card__handle" data-connect-handle title="Drag onto another card to link" aria-hidden="true">+</span>
    </article>
  `;
};

const renderMapEmptyState = (): string => `
  <div class="map-empty">
    <strong>No entities yet</strong>
    <span>Double-click the canvas or press + to create the first node.</span>
    <button type="button" data-add-entity title="Create feature" aria-label="Create feature">+</button>
  </div>
`;

const renderEntityInspector = (state: SyncrelState, entity: SyncrelEntity): string => `
  <form class="entity-inspector" data-entity-inspector="${escapeHtml(entity.id)}">
    <div class="entity-inspector__head">
      <div>
        <span>Selected card</span>
        <strong>${escapeHtml(entity.name)}</strong>
      </div>
      <button type="button" class="entity-inspector__close" data-close-inspector title="Close selected card" aria-label="Close selected card">&times;</button>
    </div>
    <label>
      <span>Name</span>
      <input data-inspector-name="${escapeHtml(entity.id)}" value="${escapeHtml(entity.name)}" />
    </label>
    <label>
      <span>Type</span>
      <select data-inspector-type="${escapeHtml(entity.id)}">
        ${["goal", "actor", "impact", "feature", "deliverable", "note"].map((type) => (
          `<option value="${escapeHtml(type)}" ${type === entity.type ? "selected" : ""}>${escapeHtml(type)}</option>`
        )).join("")}
      </select>
    </label>
    <label>
      <span>Status</span>
      <select data-inspector-status="${escapeHtml(entity.id)}">
        ${["active", "planned", "done", "archived"].map((status) => (
          `<option value="${escapeHtml(status)}" ${status === entity.status ? "selected" : ""}>${escapeHtml(status)}</option>`
        )).join("")}
      </select>
    </label>
    <div class="entity-inspector__doc-card">
      <div>
        <span>Spec</span>
        <strong>${escapeHtml(specTitle(entity))}</strong>
      </div>
      <p>${escapeHtml(docSummary(entity.doc))}</p>
      <button type="button" data-open-spec="${escapeHtml(entity.id)}">${escapeHtml(specActionLabel(entity))}</button>
    </div>
    <div class="entity-inspector__fields">
      ${activeFrameworkFields(state).length === 0
        ? `<p>No framework fields yet. Open Fields or Frameworks to add structure.</p>`
        : activeFrameworkFields(state).slice(0, 8).map((field) => renderInspectorField(entity, field)).join("")}
    </div>
    <div class="entity-inspector__actions">
      <button type="button" data-quick-source="${escapeHtml(entity.id)}" data-quick-create="right">Duplicate linked</button>
      <button type="button" data-tool-toggle="grid">Edit fields</button>
    </div>
  </form>
`;

const specActionLabel = (entity: SyncrelEntity): string => (
  entity.type === "note" || entity.type === "feedback" ? "Open note spec" : "Write spec"
);

const specTitle = (entity: SyncrelEntity): string => (
  entity.type === "note" || entity.type === "feedback" ? "Note spec" : "Product spec"
);

const docSummary = (doc: string): string => {
  const text = doc
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/[-*]\s+/g, "")
    .replace(/\s+/g, " ")
    .trim();
  if (!text) return "Start a focused spec from this canvas card.";
  return text.length > 96 ? `${text.slice(0, 93)}...` : text;
};

const renderInspectorField = (entity: SyncrelEntity, field: SchemaField): string => {
  const value = entity.fields[field.key] ?? field.defaultValue ?? "";
  const missing = field.required && (value === "" || value === undefined || value === null);
  if (field.readonly || field.type === "computed") {
    return `
      <label class="${missing ? "is-missing" : ""}">
        <span>${escapeHtml(field.label)}</span>
        <output>${escapeHtml(value)}</output>
      </label>
    `;
  }
  if (field.type === "boolean") {
    return `
      <label class="entity-inspector__check ${missing ? "is-missing" : ""}">
        <input type="checkbox" data-inspector-field-key="${escapeHtml(field.key)}" data-inspector-field-entity="${escapeHtml(entity.id)}" ${value === true ? "checked" : ""} />
        <span>${escapeHtml(field.label)}</span>
      </label>
    `;
  }
  if (field.options?.length) {
    return `
      <label class="${missing ? "is-missing" : ""}">
        <span>${escapeHtml(field.label)}</span>
        <select data-inspector-field-key="${escapeHtml(field.key)}" data-inspector-field-entity="${escapeHtml(entity.id)}">
          ${field.options.map((option) => `<option value="${escapeHtml(option)}" ${option === value ? "selected" : ""}>${escapeHtml(option)}</option>`).join("")}
        </select>
      </label>
    `;
  }
  return `
    <label class="${missing ? "is-missing" : ""}">
      <span>${escapeHtml(field.label)}</span>
      <input data-inspector-field-key="${escapeHtml(field.key)}" data-inspector-field-entity="${escapeHtml(entity.id)}" value="${escapeHtml(value)}" />
    </label>
  `;
};

const renderRelationInspector = (state: SyncrelState, relation: SyncrelRelation): string => {
  const from = state.entities.find((entity) => entity.id === relation.fromId);
  const to = state.entities.find((entity) => entity.id === relation.toId);
  return `
    <form class="relation-inspector" data-relation-form data-relation-id="${escapeHtml(relation.id)}">
      <span>${escapeHtml(from?.name ?? relation.fromId)} -> ${escapeHtml(to?.name ?? relation.toId)}</span>
      <select data-relation-type="${escapeHtml(relation.id)}" aria-label="Relation type">
        ${relationTypeOptions(state, relation.type)}
      </select>
      <input data-relation-label="${escapeHtml(relation.id)}" aria-label="Relation label" value="${escapeHtml(relation.label)}" />
      <button type="button" data-delete-relation="${escapeHtml(relation.id)}" title="Delete relation" aria-label="Delete relation">x</button>
    </form>
  `;
};

const relationTypeOptions = (state: SyncrelState, selected = "drives"): string => {
  const types = state.schema.relationTypes.length > 0
    ? state.schema.relationTypes.map((relationType) => ({ key: relationType.key, label: relationType.label }))
    : [
      { key: "drives", label: "drives" },
      { key: "blocked-by", label: "blocked by" },
      { key: "signal-origin", label: "signal origin" },
    ];
  if (!types.some((type) => type.key === selected)) types.push({ key: selected, label: selected });
  return types.map((type) => (
    `<option value="${escapeHtml(type.key)}" ${type.key === selected ? "selected" : ""}>${escapeHtml(type.label)}</option>`
  )).join("");
};
